import { useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import sabotagelogo from '@/assets/sabotage-logo.png';

// Public bounce page for the native PayPal flow. PayPal requires an http(s) return_url,
// so it cannot point at a custom scheme directly; it returns the buyer here, and this
// page forwards to the app's custom scheme. It is an UNTRUSTED forwarder: it carries no
// authority. All checks (caller JWT, pending-row exists, user_id match -> 403,
// league_id match -> 400, idempotent complete_deposit_atomic) stay in
// paypal-capture-order, invoked by the appUrlOpen handler after this hands off.
//
// Degrades gracefully everywhere else: the static "return to the app" card renders
// immediately with no spinner and no dependence on the scheme resolving.
//
// SAB-981: the scheme forward used to be attempted ONLY on iOS. That was wrong -- the
// native PayPal flow runs on Android too, and gating it there meant nothing ever captured
// an Android buyer's approved order. It is now attempted on every platform; where no app
// owns the scheme the navigation is inert and this card simply stays on screen.
//
// This page is still not the mechanism. paypal-webhook captures server-side on
// CHECKOUT.ORDER.APPROVED, so a buyer who never returns is credited anyway; the forward
// below only buys the immediate in-app confirmation when the return does arrive.


export default function PaypalReturn() {
  const [params] = useSearchParams();

  useEffect(() => {
    const token = params.get('token'); // PayPal appends ?token=<orderId>
    // SAB-981: was `if (!isIOS() || !token) return;`. That gate meant Android buyers
    // approved a payment and NOTHING ever captured it -- the app was never handed the
    // token, paypal-capture-order was never called, PayPal emitted no capture event, and
    // the deposit sat pending with the money neither taken nor returned. iOS worked only
    // because it was the one platform allowed past this line.
    //
    // The real fix is server-side: paypal-webhook now captures on CHECKOUT.ORDER.APPROVED,
    // so no platform's return is load-bearing any more. Dropping the gate is the UX half --
    // when the return DOES arrive, every platform gets the immediate capture-and-confirm
    // instead of a static card, and the two captures race harmlessly (PayPal answers the
    // loser ORDER_ALREADY_CAPTURED and the credit is idempotent on the deposit row).
    //
    // On desktop, where no app owns the scheme, the navigation is inert and the fallback
    // below stays on screen exactly as before.
    if (!token) return;

    const forward = new URLSearchParams();
    forward.set('token', token);
    const league = params.get('league');
    const cancel = params.get('cancel');
    if (league) forward.set('league', league);
    if (cancel) forward.set('cancel', cancel);

    // Fire-and-forget deep link. If the app is installed, iOS routes the scheme and the
    // app takes over (its appUrlOpen handler captures the order). If not, nothing
    // happens and the fallback below stays on screen. No hang either way.
    window.location.href = `com.sabotagefantasy.app://paypal-return?${forward.toString()}`;
  }, [params]);

  return (
    <div className="min-h-screen bg-gradient-dark flex items-center justify-center px-4">
      <Card className="w-full max-w-md border-border bg-card">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <img src={sabotagelogo} alt="Sabotage Fantasy" className="h-20 w-auto" />
          </div>
          <CardTitle className="text-2xl font-bold text-foreground font-display">
            Payment approved — finishing your deposit
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {/* SAB-1093/1091: this page CANNOT confirm the deposit itself. It renders in an
                in-app browser with no session, and the only SELECT policy on
                wallet_transactions is auth.uid() = user_id, so it cannot read the row — a
                poll here would return nothing no matter how long it ran. What it can do is
                state what is actually true: PayPal has the approval, paypal-webhook captures
                and credits server-side on CHECKOUT.ORDER.APPROVED, and that happens whether
                or not the user ever comes back to this page. Previously this said "Open the
                app to finish your deposit", which implied the deposit was NOT finished and
                depended on the user — the opposite of how it works. */}
            Your payment went through. We're crediting your wallet now — this happens
            automatically, even if you close this page. Open the app or check your wallet to
            see the new balance.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild className="w-full">
            <Link to="/wallet">Go to Wallet</Link>
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
